import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    setFormStatus('sending');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9qqbdfnahb7382ovdig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '鬼父2解析站',
        url: 'https://www.guifu2-analysis.com/',
        description: '专注于鬼父2动画系列的深度内容解析平台，提供专业的剧情分析、人物解读和主题探讨。',
        foundingDate: '2025',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@guifu2-analysis.com',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 鬼父2解析站',
        description: '了解鬼父2解析站的品牌故事、信誉保障和联系方式。',
        url: 'https://www.guifu2-analysis.com/about',
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '鬼父2解析站提供什么内容？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '鬼父2解析站专注于鬼父2动画系列的深度内容解析，提供专业的剧情分析、人物解读、经典场景回顾、主题探讨及续集看点等内容。',
            },
          },
          {
            '@type': 'Question',
            name: '如何联系鬼父2解析站的编辑团队？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '您可以通过页面上的联系表单或发送邮件至 contact@guifu2-analysis.com 与我们取得联系。',
            },
          },
          {
            '@type': 'Question',
            name: '鬼父2解析站的内容多久更新一次？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '我们的编辑团队保持每周至少更新2-3篇高质量解析文章，确保为读者提供持续、深度的内容。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-8 pb-8">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
              关于我们
            </h1>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-16">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>
                  鬼父2解析站创立于2025年，是国内最早专注于鬼父2动画系列深度解析的专业内容平台之一。我们汇聚了一批对动画艺术充满热情的内容创作者，致力于为观众提供专业、深入、有价值的作品解析。
                </p>
                <p>
                  我们的核心理念是"深度解读，理性分析"。不同于泛泛而谈的观点输出，我们的每一篇文章都经过严格的内容审核和多轮修改，力求在专业性和可读性之间找到最佳平衡点。无论您是初次接触鬼父2的新观众，还是已经反复观看多遍的资深粉丝，都能在这里找到有价值的内容。
                </p>
                <p>
                  在内容创作上，我们坚持独立、客观的立场。我们的分析文章不受任何商业利益的影响，所有的观点和结论都建立在严谨的文本分析和艺术理论之上。我们希望成为一个值得信赖的内容来源，为鬼父2的观众群体提供一个理性交流的空间。
                </p>
                <p>
                  目前，鬼父2解析站已经累计发表了超过12篇深度解析文章，涵盖剧情梳理、人物分析、场景解读、主题探讨和幕后制作等多个维度。我们的内容被多家动漫社区和社交媒体平台广泛转载，月度活跃读者超过50,000人次。
                </p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  站点信息
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-calendar-check-line"></i>
                    </div>
                    <div>
                      <span className="text-foreground-500 text-xs">创立时间</span>
                      <p className="text-foreground-800 font-medium">2025年</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-article-line"></i>
                    </div>
                    <div>
                      <span className="text-foreground-500 text-xs">累计文章</span>
                      <p className="text-foreground-800 font-medium">12+ 篇深度解析</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-user-heart-line"></i>
                    </div>
                    <div>
                      <span className="text-foreground-500 text-xs">月度读者</span>
                      <p className="text-foreground-800 font-medium">50,000+ 人次</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-stack-line"></i>
                    </div>
                    <div>
                      <span className="text-foreground-500 text-xs">内容分类</span>
                      <p className="text-foreground-800 font-medium">5 大板块</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              信誉保障
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '专业审核',
                  desc: '每篇文章均经过多名编辑交叉审核，确保内容准确性和分析的严谨性。',
                },
                {
                  icon: 'ri-pencil-ruler-line',
                  title: '深度原创',
                  desc: '所有内容均为原创撰写，拒绝洗稿和拼凑，保证每篇文章的独特价值。',
                },
                {
                  icon: 'ri-eye-line',
                  title: '客观透明',
                  desc: '坚持独立立场，不受商业利益影响，为读者提供最真实的分析视角。',
                },
                {
                  icon: 'ri-refresh-line',
                  title: '持续更新',
                  desc: '保持每周至少2-3篇的更新频率，确保内容的时效性和持续性。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-background-100 rounded-xl p-6 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="max-w-[640px] mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">电子邮箱</p>
                    <p className="text-sm text-foreground-800 font-medium">contact@guifu2-analysis.com</p>
                  </div>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">微信公众号</p>
                    <p className="text-sm text-foreground-800 font-medium">鬼父2解析站</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">
                  发送消息
                </h3>
                <form
                  className="space-y-4"
                  data-readdy-form="about-contact-form"
                  onSubmit={handleSubmit}
                >
                  <div className="antispam-field">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的消息（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>

                  {formStatus === 'success' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                      <i className="ri-checkbox-circle-fill text-lg"></i>
                      消息已成功发送！感谢您的来信，我们会尽快回复。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-100 text-primary-800 text-sm">
                      <i className="ri-error-warning-fill text-lg"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full sm:w-auto px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'sending' ? (
                      <span className="flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i> 发送中...
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}